import {normalizeLinkTarget} from './reviews-link-action-rules';

export const DEFAULT_REVIEWS_BRICK_CONFIG = {
    insertType: 'brick',
    mode: 'group',
    groupKey: 'all',
    sourceKey: '',
    scope: 'website',
    variant: 'inline',
    logo: true,
    precision: 1,
    showCount: true,
    showRating: true,
    showStars: true,
    parenCount: true,
    countFormat: 'short',
    copyStyle: 'none',
    copyText: '',
    shortCount: false,
    link: true,
    linkTarget: '',
    customUrl: ''
};

const decodeToken = (text) => {
    if (!text) return '';
    return String(text).trim()
        .replace(/&amp;gt;/g, '>')
        .replace(/&gt;/g, '>');
};

const extractHashValue = (token, keys = []) => {
    for (const key of keys) {
        const match = new RegExp(`\\b${key}\\s*=\\s*(?:\"([^\"]*)\"|'([^']*)'|(\\S+))`, 'i').exec(token);
        if (match) {
            return match[1] ?? match[2] ?? match[3] ?? '';
        }
    }
    return null;
};

export const parseReviewsBrickToken = (text) => {
    const token = decodeToken(text);
    if (!token) return null;
    if (!token.startsWith('{{')) return null;

    const groupMatch = /reviews\.google\.group:([a-zA-Z0-9_-]+)/i.exec(token);
    const sourceMatch = /reviews\.google\.source:([a-zA-Z0-9_-]+)/i.exec(token);
    if (!groupMatch && !sourceMatch) return null;

    const scopeMatch = /\bscope\s*=\s*([a-zA-Z0-9_-]+)/i.exec(token);
    const precisionMatch = /\bprecision\s*=\s*([0-9]+)/i.exec(token);
    const variantMatch = /\bvariant\s*=\s*([a-zA-Z0-9_-]+)/i.exec(token);
    const showCountMatch = /\bshowCount\s*=\s*(true|false)\b/i.exec(token);
    const hideCountMatch = /\bhideCount\s*=\s*(true|false)\b/i.exec(token);
    const showRatingMatch = /\bshowRating\s*=\s*(true|false)\b/i.exec(token);
    const showStarsMatch = /\bshowStars\s*=\s*(true|false)\b/i.exec(token);
    const parenCountMatch = /\bparenCount\s*=\s*(true|false)\b/i.exec(token);
    const countFormatMatch = /\bcountFormat\s*=\s*([a-z-]+)/i.exec(token);
    const formatMatch = /\bformat\s*=\s*([a-z-]+)/i.exec(token);
    const logoMatch = /\b(?:logo|showLogo)\s*=\s*(true|false)\b/i.exec(token);
    const copyValue = extractHashValue(token, ['copy', 'suffix']);
    const copyTextValue = extractHashValue(token, ['copyText', 'suffixText']);
    const linkMatch = /\blink\s*=\s*(true|false)\b/i.exec(token);
    const linkTargetValue = extractHashValue(token, ['linkTarget', 'linkMode']);
    const customUrlValue = extractHashValue(token, ['customUrl', 'url', 'href']);
    const shortMatch = /\bshort(?:Count)?\s*=\s*(true|false)\b/i.exec(token);

    let showCount = true;
    if (showCountMatch) showCount = showCountMatch[1].toLowerCase() !== 'false';
    if (hideCountMatch) showCount = hideCountMatch[1].toLowerCase() === 'false';

    let showRating = true;
    if (showRatingMatch) showRating = showRatingMatch[1].toLowerCase() !== 'false';

    let showStars = true;
    if (showStarsMatch) showStars = showStarsMatch[1].toLowerCase() !== 'false';

    let parenCount = true;
    if (parenCountMatch) parenCount = parenCountMatch[1].toLowerCase() !== 'false';

    const legacyLinkEnabled = linkMatch ? linkMatch[1].toLowerCase() !== 'false' : true;
    const linkTarget = normalizeLinkTarget(linkTargetValue, legacyLinkEnabled);
    const scope = scopeMatch ? scopeMatch[1] : 'website';
    const customUrl = String(customUrlValue || '').trim();
    const safeCustomUrl = /^javascript:/i.test(customUrl) ? '' : customUrl;
    const link = linkTarget !== 'none';
    const shortCount = shortMatch ? shortMatch[1].toLowerCase() === 'true' : false;
    const countFormat = (countFormatMatch?.[1] || formatMatch?.[1] || '').toLowerCase();
    const copyStyle = String(copyValue || 'on-google').toLowerCase();
    const copyText = String(copyTextValue || '');
    const logo = logoMatch ? logoMatch[1].toLowerCase() === 'true' : false;

    const parsedVariant = (variantMatch ? variantMatch[1] : 'inline').toLowerCase();
    const variant = parsedVariant === 'compact' ? 'inline' : parsedVariant;
    const safeVariant = ['inline', 'pill', 'outline'].includes(variant) ? variant : 'inline';

    return {
        insertType: 'brick',
        mode: sourceMatch ? 'single' : 'group',
        groupKey: sourceMatch ? 'all' : groupMatch[1],
        sourceKey: sourceMatch ? sourceMatch[1] : '',
        scope,
        precision: precisionMatch ? Number(precisionMatch[1]) : 1,
        variant: safeVariant,
        logo,
        showCount,
        showRating,
        showStars,
        parenCount,
        link,
        linkTarget,
        customUrl: safeCustomUrl,
        shortCount,
        countFormat: countFormat || (shortCount ? 'short' : 'full'),
        copyStyle,
        copyText
    };
};

export const buildReviewsBrickToken = (config = {}) => {
    const merged = {...DEFAULT_REVIEWS_BRICK_CONFIG, ...config};
    const sourceKey = String(merged.sourceKey || '').trim();
    const groupKey = String(merged.groupKey || '').trim();
    if (!sourceKey && !groupKey) return null;
    const precision = Number.isFinite(Number(merged.precision)) ? Number(merged.precision) : 1;
    const scopeSegment = merged.scope && merged.scope !== 'website'
        ? `scope=${merged.scope}`
        : null;

    const variant = (merged.variant || 'inline').toLowerCase();
    const safeVariant = ['inline', 'pill', 'outline'].includes(variant) ? variant : 'inline';
    const legacyLinkEnabled = merged.link !== false;
    const configuredLinkTarget = String(merged.linkTarget || '').toLowerCase();
    const resolvedLinkTarget = normalizeLinkTarget(configuredLinkTarget, legacyLinkEnabled);
    const customUrl = String(merged.customUrl || '').trim();
    const safeCustomUrl = /^javascript:/i.test(customUrl) ? '' : customUrl;
    const sourceToken = sourceKey
        ? `reviews.google.source:${sourceKey}`
        : `reviews.google.group:${groupKey}`;

    const parts = [
        `{{>rating.stars ${sourceToken}`,
        `precision=${precision}`,
        `variant=${safeVariant}`
    ];

    if (!merged.showCount) {
        parts.push('showCount=false');
    }

    if (!merged.showRating) {
        parts.push('showRating=false');
    }

    if (!merged.showStars) {
        parts.push('showStars=false');
    }

    if (!merged.parenCount) {
        parts.push('parenCount=false');
    }

    if (merged.logo) {
        parts.push('logo=true');
    }

    if (merged.countFormat && merged.countFormat !== 'full') {
        parts.push(`countFormat=${merged.countFormat}`);
    }

    if (merged.copyStyle && merged.copyStyle !== 'on-google') {
        parts.push(`copy=${merged.copyStyle}`);
    }

    if (String(merged.copyStyle || '').toLowerCase() === 'custom') {
        const copyText = String(merged.copyText || '').trim();
        if (copyText) {
            parts.push(`copyText=${JSON.stringify(copyText)}`);
        }
    }

    if (resolvedLinkTarget === 'none') {
        parts.push('link=false');
    }

    if (resolvedLinkTarget === 'custom') {
        parts.push('linkTarget=custom');
        if (safeCustomUrl) {
            parts.push(`customUrl=${JSON.stringify(safeCustomUrl)}`);
        }
    }

    if (scopeSegment) {
        parts.push(scopeSegment);
    }

    parts.push('}}');
    return parts.join(' ');
};
